import { brand } from "../../../../config/theme";
import {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
  memo,
} from "react";
import { createPortal } from "react-dom";
import { useJsApiLoader, GoogleMap } from "@react-google-maps/api";
import { latLngToCell } from "h3-js";
import { getTimeAgo } from "../../../../utils/getDate";
import type { UnitData, ActiveUser } from "../../../../store/public/publicTrackingLinkStore";
import { usePublicTrackingLinkStore } from "../../../../store/public/publicTrackingLinkStore";
import { PublicTrackingSidebar } from "./PublicTrackingSidebar";
import {
  normalizeStatus,
  colorForStatus,
  labelForStatus,
  statusBadgeStyle,
  STATUS_COLOR,
  rgba,
  type StatusCategory,
} from "./publicTrackingStatus";

const DEFAULT_CENTER = { lat: 23.6345, lng: -102.5528 };
const DEFAULT_ZOOM = 5;

// Zoom al enfocar una unidad (click en pin o card). Igual espíritu que MapView:
// nos acercamos, pero solo si el usuario está más lejos (nunca alejamos).
const FOCUS_ZOOM = 16;
// Desplazamiento horizontal del centro para que el panel de la derecha no tape
// la unidad enfocada (mismo mecanismo que MapView / FOCUS_PANEL_OFFSET_X).
const FOCUS_PANEL_OFFSET_X = -180;
// Zoom al que se acerca al hacer click en un clúster.
const CLUSTER_ZOOM = 11;

// Marker sizes matching MapView
const MARKER_SIZE = 32;
const DRIVING_MARKER_SIZE = 30;
const DRIVING_BORDER_WIDTH = 3;
const CLUSTER_SIZE = 40;

const CUSTOM_MARKER_OFFSET = {
  x: 0,
  y: 0,
};

const CLUSTER_MARKER_OFFSET = {
  x: -(CLUSTER_SIZE / 2),
  y: -(CLUSTER_SIZE / 2),
};

// Mapeo zoom -> resolución H3 (mismo que MapView)
const H3_ZOOM_MAPPING: { [key: number]: number } = {
  4: 1, 5: 2, 6: 2, 7: 3, 8: 3, 9: 4, 10: 5, 11: 6, 12: 6, 13: 7,
  14: 8, 15: 8, 16: 9, 17: 10, 18: 10, 19: 11, 20: 12, 21: 13, 22: 13,
};

const getH3Resolution = (zoom: number): number => {
  const normalizedZoom = Math.floor(zoom);
  return H3_ZOOM_MAPPING[normalizedZoom] || 7;
};

// Desplaza el centro del mapa horizontalmente (en px) para que la unidad quede
// a la izquierda del panel. Copiado de MapView.panMapToVehicle.
const panMapToVehicle = (
  map: google.maps.Map,
  lat: number,
  lng: number,
  offsetX: number,
) => {
  const projection = map.getProjection();
  const zoom = map.getZoom();
  if (!offsetX || !projection || zoom == null) {
    map.panTo({ lat, lng });
    return;
  }
  const worldPoint = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng));
  if (!worldPoint) {
    map.panTo({ lat, lng });
    return;
  }
  const scale = Math.pow(2, zoom);
  const shifted = new google.maps.Point(worldPoint.x - offsetX / scale, worldPoint.y);
  const newCenter = projection.fromPointToLatLng(shifted);
  map.panTo(newCenter ?? { lat, lng });
};

// Marker shape matching MapView
const getMarkerShape = (status: string, course: number | null | undefined) => {
  const category = normalizeStatus(status);
  if (category === "driving") {
    // La flecha solo tiene sentido si hay heading real. Cuando el provider manda
    // únicamente lat/lon (caso RDB) course queda en 0/null y la flecha apuntaría
    // siempre al norte engañando al usuario. En ese caso usamos el shape default
    // (círculo con truck), pero el color sigue siendo el de "driving".
    const hasHeading = course != null && course !== 0;
    if (!hasHeading) {
      return {
        icon: "ri-truck-line",
        borderRadius: "50%",
        clipPath: undefined,
        size: MARKER_SIZE,
        hasBorder: false,
        borderWidth: 0,
      };
    }
    return {
      icon: null,
      clipPath: "polygon(50% 0%, 100% 100%, 50% 75%, 0% 100%)",
      borderRadius: undefined,
      size: DRIVING_MARKER_SIZE,
      hasBorder: true,
      borderWidth: DRIVING_BORDER_WIDTH,
    };
  }
  if (category === "stopped") {
    return {
      icon: "ri-stop-line",
      borderRadius: "0%",
      clipPath: undefined,
      size: MARKER_SIZE,
      hasBorder: false,
      borderWidth: 0,
    };
  }
  if (category === "ralenti") {
    return {
      icon: "ri-pause-circle-line",
      borderRadius: "50%",
      clipPath: undefined,
      size: MARKER_SIZE,
      hasBorder: false,
      borderWidth: 0,
    };
  }
  // offline / default
  return {
    icon: "ri-truck-line",
    borderRadius: "50%",
    clipPath: undefined,
    size: MARKER_SIZE,
    hasBorder: false,
    borderWidth: 0,
  };
};

const FOCUS_GLOW = rgba(brand.primary, 0.45);

// Color del clúster según el estado predominante (igual que MapView).
const getClusterColor = (vehicles: UnitData[]): string => {
  if (!vehicles || vehicles.length === 0) return STATUS_COLOR.offline;
  const counts: Record<string, number> = {};
  vehicles.forEach((v) => {
    const c = normalizeStatus(v.status);
    counts[c] = (counts[c] || 0) + 1;
  });
  const dominant = Object.entries(counts).sort(
    (a, b) => b[1] - a[1],
  )[0][0] as StatusCategory;
  return STATUS_COLOR[dominant];
};

// Marker nativo: usa google.maps.OverlayView + createPortal igual que MapView.
// Esto evita el parpadeo / corrimiento que produce el OverlayView de
// @react-google-maps/api al re-renderizar React en cada pan/zoom: aquí la
// posición la actualiza la clase OverlayView directamente vía updatePosition,
// sin pasar por el ciclo de render de React.
interface VehicleMarkerProps {
  vehicle: UnitData;
  onClick: () => void;
  isFocused: boolean;
  map: google.maps.Map | null;
}

const VehicleMarker = memo(
  ({ vehicle, onClick, isFocused, map }: VehicleMarkerProps) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);

    const markerColor = useMemo(
      () => colorForStatus(vehicle.status),
      [vehicle.status],
    );
    const markerStyle = useMemo(
      () => getMarkerShape(vehicle.status, vehicle.course),
      [vehicle.status, vehicle.course],
    );

    useEffect(() => {
      if (!map) return;

      const div = document.createElement("div");
      div.style.position = "absolute";

      class CustomOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement;

        constructor(position: google.maps.LatLng, div: HTMLDivElement) {
          super();
          this.position = position;
          this.div = div;
        }

        onAdd() {
          const panes = this.getPanes();
          if (panes) {
            panes.overlayMouseTarget.appendChild(this.div);
          }
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;
          const pos = projection.fromLatLngToDivPixel(this.position);
          if (pos) {
            this.div.style.transform = "translate(-50%, -50%)";
            this.div.style.left = `${pos.x + CUSTOM_MARKER_OFFSET.x}px`;
            this.div.style.top = `${pos.y + CUSTOM_MARKER_OFFSET.y}px`;
          }
        }

        onRemove() {
          if (this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
        }

        updatePosition(newPosition: google.maps.LatLng) {
          this.position = newPosition;
          this.draw();
        }
      }

      const position = new google.maps.LatLng(
        Number(vehicle.latitude),
        Number(vehicle.longitude),
      );

      const overlay = new CustomOverlay(position, div);
      overlay.setMap(map);
      overlayRef.current = overlay;
      setContainer(div);

      return () => {
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
        }
        setContainer(null);
      };
      // Recrear overlay solo si cambia el mapa o la unidad — la posición se
      // actualiza por el otro effect sin recrear nada.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, vehicle.id]);

    // Update position cuando llegan nuevas coords sin recrear el overlay.
    useEffect(() => {
      if (overlayRef.current && vehicle.latitude && vehicle.longitude) {
        const newPosition = new google.maps.LatLng(
          Number(vehicle.latitude),
          Number(vehicle.longitude),
        );
        (overlayRef.current as unknown as { updatePosition: (p: google.maps.LatLng) => void })
          .updatePosition(newPosition);
      }
    }, [vehicle.latitude, vehicle.longitude]);

    // La unidad enfocada debe pasar al frente para que su glow no quede tapado
    // por pines vecinos (los markers viven todos en el mismo pane).
    useEffect(() => {
      if (container) container.style.zIndex = isFocused ? "1000" : "";
    }, [container, isFocused]);

    if (!container) return null;

    return createPortal(
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className="cursor-pointer"
        style={{
          width: `${markerStyle.size}px`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          className={`absolute bottom-full left-1/2 -translate-x-1/2 text-white text-[10px] px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap ${
            isFocused ? "" : "bg-gray-900"
          }`}
          style={{
            marginBottom: "2px",
            backgroundColor: isFocused ? brand.primary : undefined,
            color: isFocused ? "#111827" : undefined,
          }}
        >
          {vehicle.name}
        </div>
        {markerStyle.hasBorder && markerStyle.clipPath ? (
          <div
            style={{
              position: "relative",
              width: `${markerStyle.size}px`,
              height: `${markerStyle.size}px`,
              transform: `rotate(${vehicle.course ?? 0}deg)`,
              filter: isFocused
                ? `drop-shadow(0 0 4px ${FOCUS_GLOW}) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))`
                : "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: isFocused ? brand.primary : "#ffffff",
                clipPath: markerStyle.clipPath,
              }}
            />
            <div
              style={{
                position: "absolute",
                top: `${markerStyle.borderWidth}px`,
                left: `${markerStyle.borderWidth}px`,
                width: `calc(100% - ${markerStyle.borderWidth * 2}px)`,
                height: `calc(100% - ${markerStyle.borderWidth * 2}px)`,
                backgroundColor: markerColor,
                clipPath: markerStyle.clipPath,
              }}
            />
          </div>
        ) : (
          <div
            style={{
              width: `${markerStyle.size}px`,
              height: `${markerStyle.size}px`,
              backgroundColor: markerColor,
              borderRadius: markerStyle.borderRadius,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `3px solid ${isFocused ? brand.primary : "#ffffff"}`,
              boxShadow: isFocused
                ? `0 0 0 4px ${FOCUS_GLOW}, 0 2px 4px rgba(0, 0, 0, 0.3)`
                : "0 2px 4px rgba(0, 0, 0, 0.3)",
            }}
          >
            {markerStyle.icon && (
              <i className={`${markerStyle.icon} text-white text-sm`}></i>
            )}
          </div>
        )}
      </div>,
      container,
    );
  },
  (prev, next) =>
    prev.vehicle.id === next.vehicle.id &&
    prev.vehicle.latitude === next.vehicle.latitude &&
    prev.vehicle.longitude === next.vehicle.longitude &&
    prev.vehicle.status === next.vehicle.status &&
    prev.vehicle.course === next.vehicle.course &&
    prev.isFocused === next.isFocused &&
    prev.map === next.map,
);

// Clúster nativo (mismo overlay que VehicleMarker). Muestra el conteo y usa el
// color del estado predominante.
interface ClusterData {
  id: string;
  vehicles: UnitData[];
  center: { lat: number; lng: number };
  count: number;
  isCluster: boolean;
}

interface ClusteredMarkerProps {
  cluster: ClusterData;
  onClick: () => void;
  map: google.maps.Map | null;
}

const ClusteredMarker = memo(
  ({ cluster, onClick, map }: ClusteredMarkerProps) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);

    const clusterColor = useMemo(
      () => getClusterColor(cluster?.vehicles || []),
      [cluster?.vehicles],
    );

    useEffect(() => {
      if (!map || !cluster?.center) return;

      const div = document.createElement("div");
      div.style.position = "absolute";

      class ClusterOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement;

        constructor(position: google.maps.LatLng, div: HTMLDivElement) {
          super();
          this.position = position;
          this.div = div;
        }

        onAdd() {
          const panes = this.getPanes();
          if (panes) {
            panes.overlayMouseTarget.appendChild(this.div);
          }
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;
          const pos = projection.fromLatLngToDivPixel(this.position);
          if (pos) {
            this.div.style.left = `${pos.x + CLUSTER_MARKER_OFFSET.x}px`;
            this.div.style.top = `${pos.y + CLUSTER_MARKER_OFFSET.y}px`;
          }
        }

        onRemove() {
          if (this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
        }

        updatePosition(newPosition: google.maps.LatLng) {
          this.position = newPosition;
          this.draw();
        }
      }

      const position = new google.maps.LatLng(
        Number(cluster.center.lat),
        Number(cluster.center.lng),
      );

      const overlay = new ClusterOverlay(position, div);
      overlay.setMap(map);
      overlayRef.current = overlay;
      setContainer(div);

      return () => {
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
        }
        setContainer(null);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, cluster?.id]);

    useEffect(() => {
      if (overlayRef.current && cluster?.center) {
        const newPosition = new google.maps.LatLng(
          Number(cluster.center.lat),
          Number(cluster.center.lng),
        );
        (overlayRef.current as unknown as { updatePosition: (p: google.maps.LatLng) => void })
          .updatePosition(newPosition);
      }
    }, [cluster?.center?.lat, cluster?.center?.lng]);

    if (!cluster || !cluster.center || cluster.count <= 0 || !container) {
      return null;
    }

    return createPortal(
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className="relative flex items-center justify-center cursor-pointer"
        style={{ width: `${CLUSTER_SIZE}px`, height: `${CLUSTER_SIZE}px` }}
      >
        <div
          className="absolute rounded-full opacity-20"
          style={{
            width: `${CLUSTER_SIZE * 2}px`,
            height: `${CLUSTER_SIZE * 2}px`,
            backgroundColor: clusterColor,
          }}
        />
        <div
          className="absolute rounded-full opacity-40"
          style={{
            width: `${CLUSTER_SIZE * 1.5}px`,
            height: `${CLUSTER_SIZE * 1.5}px`,
            backgroundColor: clusterColor,
          }}
        />
        <div
          className="relative rounded-full flex items-center justify-center shadow-lg border-2 border-white"
          style={{
            width: `${CLUSTER_SIZE}px`,
            height: `${CLUSTER_SIZE}px`,
            backgroundColor: clusterColor,
          }}
        >
          <span className="text-white font-bold text-sm">{cluster.count || 0}</span>
        </div>
      </div>,
      container,
    );
  },
  (prev, next) =>
    prev.cluster?.id === next.cluster?.id &&
    prev.cluster?.center?.lat === next.cluster?.center?.lat &&
    prev.cluster?.center?.lng === next.cluster?.center?.lng &&
    prev.cluster?.count === next.cluster?.count &&
    prev.map === next.map,
);

interface PublicTrackingMapProps {
  vehicles: UnitData[];
  activeUsers: ActiveUser[];
  linkName: string;
}

export function PublicTrackingMap({
  vehicles: initialVehicles,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  linkName,
}: PublicTrackingMapProps) {
  const [selectedVehicle, setSelectedVehicle] = useState<UnitData | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  // Estado (no ref) para que el cambio de "map ready" trigger re-render y los
  // markers nativos puedan montarse — los markers necesitan el map instance.
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(DEFAULT_ZOOM);

  // Socket connection status from the public tracking store
  const isSocketConnected = usePublicTrackingLinkStore((state) => state.isSocketConnected);

  // Use vehicles directly from the store (updated in real-time via socket)
  const vehicles = initialVehicles;

  // Filtrar vehículos por estado y coordenadas válidas para el mapa
  const filteredVehicles = useMemo(() => {
    let filtered = vehicles.filter(
      (v) =>
        v.latitude !== 0 &&
        v.longitude !== 0 &&
        v.latitude != null &&
        v.longitude != null,
    );

    if (statusFilter !== "all") {
      filtered = filtered.filter((v) => normalizeStatus(v.status) === statusFilter);
    }

    return filtered;
  }, [vehicles, statusFilter]);

  // Agrupar en clústeres para zoom bajo (igual criterio que MapView: a partir
  // de zoom 10 se muestran individuales).
  const groupedVehicles = useMemo<ClusterData[]>(() => {
    const zoom = currentZoom || DEFAULT_ZOOM;

    if (zoom >= 10) {
      return filteredVehicles.map((v) => ({
        id: `i_${v.id}`,
        vehicles: [v],
        center: { lat: v.latitude, lng: v.longitude },
        count: 1,
        isCluster: false,
      }));
    }

    const resolution = getH3Resolution(zoom);
    const groups = new Map<string, UnitData[]>();

    filteredVehicles.forEach((v) => {
      try {
        const cell = latLngToCell(v.latitude, v.longitude, resolution);
        if (!groups.has(cell)) groups.set(cell, []);
        groups.get(cell)!.push(v);
      } catch {
        // coordenadas inválidas → omitir
      }
    });

    return Array.from(groups.entries()).map(([cell, vs]) => {
      const lat = vs.reduce((s, v) => s + v.latitude, 0) / vs.length;
      const lng = vs.reduce((s, v) => s + v.longitude, 0) / vs.length;
      return {
        id: cell,
        vehicles: vs,
        center: { lat, lng },
        count: vs.length,
        isCluster: vs.length > 1,
      };
    });
  }, [filteredVehicles, currentZoom]);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      setMapInstance(map);
      mapRef.current = map;

      // Encuadre inicial: si hay varias unidades con posición, fitBounds sobre
      // todas; si hay una sola, centrar en ella; si ninguna, quedarse en el
      // default (México). Antes se centraba ciegamente en vehicles[0].
      const valid = vehicles.filter((v) => v.latitude && v.longitude);
      if (valid.length === 1) {
        map.setCenter({ lat: valid[0].latitude, lng: valid[0].longitude });
        map.setZoom(15);
      } else if (valid.length > 1) {
        const bounds = new google.maps.LatLngBounds();
        valid.forEach((v) => bounds.extend({ lat: v.latitude, lng: v.longitude }));
        map.fitBounds(bounds, 80);
      }
      setCurrentZoom(map.getZoom() ?? DEFAULT_ZOOM);
    },
    [vehicles],
  );

  const onUnmount = useCallback(() => {
    setMapInstance(null);
    mapRef.current = null;
  }, []);

  const handleZoomChanged = useCallback(() => {
    const z = mapRef.current?.getZoom();
    if (z != null) setCurrentZoom(z);
  }, []);

  const getStatusColor = colorForStatus;
  const getStatusText = labelForStatus;

  const formatLastReport = (lastReport: any) => {
    if (!lastReport) return "Sin datos";
    // getTimeAgo espera SEGUNDOS.
    let timestampInSeconds: number;
    if (lastReport._seconds) {
      timestampInSeconds = lastReport._seconds;
    } else if (typeof lastReport === "number") {
      // El socket manda milisegundos → convertir a segundos.
      timestampInSeconds = Math.floor(lastReport / 1000);
    } else {
      timestampInSeconds = Math.floor(new Date(lastReport).getTime() / 1000);
    }
    if (isNaN(timestampInSeconds) || timestampInSeconds <= 0) return "Sin datos";
    return getTimeAgo(timestampInSeconds);
  };

  // Enfocar una unidad: seleccionar + acercar (sin alejar) + desplazar el centro
  // para que el panel de la derecha no la tape. Mismo comportamiento para el
  // click en el pin y en la card.
  const focusVehicle = useCallback((vehicle: UnitData) => {
    setSelectedVehicle(vehicle);
    const map = mapRef.current;
    if (!map || !vehicle.latitude || !vehicle.longitude) return;
    const current = map.getZoom() ?? DEFAULT_ZOOM;
    if (current < FOCUS_ZOOM) map.setZoom(FOCUS_ZOOM);
    panMapToVehicle(map, vehicle.latitude, vehicle.longitude, FOCUS_PANEL_OFFSET_X);
  }, []);

  const handleSelectVehicle = useCallback(
    (vehicle: UnitData | null) => {
      if (!vehicle) {
        setSelectedVehicle(null);
        return;
      }
      focusVehicle(vehicle);
    },
    [focusVehicle],
  );

  const handleClusterClick = useCallback((cluster: ClusterData) => {
    const map = mapRef.current;
    if (!map || !cluster?.center) return;
    map.panTo(cluster.center);
    map.setZoom(CLUSTER_ZOOM);
    setSelectedVehicle(null);
  }, []);

  if (!isLoaded) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Cargando mapa...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center text-white">
          <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
          <p>Error al cargar el mapa</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full flex">
      {/* Sidebar */}
      <PublicTrackingSidebar
        units={vehicles}
        activeUsers={[]}
        linkName={linkName}
        selectedUnit={selectedVehicle}
        onSelectUnit={handleSelectVehicle}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        selectedFilter={statusFilter}
        onFilterChange={setStatusFilter}
      />

      {/* Map Container */}
      <div className="flex-1 relative">
        {/* Real-time indicator */}
        <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-lg px-3 py-2 flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${isSocketConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
          <span className="text-xs font-medium text-gray-700">{isSocketConnected ? 'Tiempo real' : 'Conectando...'}</span>
        </div>

        {/* Map */}
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onZoomChanged={handleZoomChanged}
          onIdle={handleZoomChanged}
          options={{
            minZoom: 3,
            maxZoom: 20,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            zoomControl: true,
            zoomControlOptions: {
              position: google.maps.ControlPosition.RIGHT_CENTER,
            },
          }}
        >
          {mapInstance &&
            groupedVehicles.map((group) =>
              group.isCluster ? (
                <ClusteredMarker
                  key={group.id}
                  cluster={group}
                  map={mapInstance}
                  onClick={() => handleClusterClick(group)}
                />
              ) : (
                <VehicleMarker
                  key={group.vehicles[0].id}
                  vehicle={group.vehicles[0]}
                  map={mapInstance}
                  isFocused={selectedVehicle?.id === group.vehicles[0].id}
                  onClick={() => focusVehicle(group.vehicles[0])}
                />
              ),
            )}
        </GoogleMap>

        {/* Vehicle Info Panel — a la derecha, verticalmente centrado (como MapView) */}
        {selectedVehicle && (
          <div className="absolute top-1/2 -translate-y-1/2 right-4 left-4 sm:left-auto sm:w-96 max-h-[calc(100%-2rem)] overflow-y-auto bg-white rounded-xl shadow-2xl border border-gray-200 z-20">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-white"
                  style={{
                    backgroundColor: getStatusColor(selectedVehicle.status),
                  }}
                >
                  <i className="ri-truck-line text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    {selectedVehicle.name}
                  </h3>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={statusBadgeStyle(selectedVehicle.status)}
                  >
                    {getStatusText(selectedVehicle.status)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedVehicle(null)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-lg text-gray-500"></i>
              </button>
            </div>

            {/* Status Icons Row */}
            <div className="py-4 px-4 border-b border-gray-200">
              <div className="flex items-start justify-between gap-2">
                {[
                  {
                    icon: "ri-time-line",
                    label: "Última conexión",
                    value: formatLastReport(selectedVehicle.lastReport),
                    color: "#009804",
                  },
                  {
                    icon: "ri-dashboard-3-line",
                    label: "Odómetro",
                    value:
                      selectedVehicle.odometer != null
                        ? `${Math.round(selectedVehicle.odometer).toLocaleString()} km`
                        : "Sin datos",
                    color: "#009804",
                  },
                  {
                    icon: "ri-speed-line",
                    label: "Velocidad",
                    value:
                      selectedVehicle.speed != null
                        ? `${selectedVehicle.speed.toFixed(0)} km/h`
                        : "Sin datos",
                    color: "#009804",
                  },
                  {
                    icon: "ri-key-line",
                    label: "Ignición",
                    value: selectedVehicle.ignition ? "Encendida" : "Apagada",
                    color: selectedVehicle.ignition ? "#22C55E" : "#9CA3AF",
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center text-center flex-1 min-w-0"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#ECFFE6] flex items-center justify-center mb-1.5">
                      <i
                        className={`${item.icon} text-lg`}
                        style={{ color: item.color }}
                      ></i>
                    </div>
                    <div className="w-8 h-0.5 bg-gray-300 mb-1" />
                    <span className="text-xs font-semibold text-gray-900 leading-tight truncate w-full">
                      {item.value}
                    </span>
                    <span className="text-[10px] text-gray-400 mt-0.5">
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Location */}
            <div className="p-4 space-y-3">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">
                  Dirección:
                </h4>
                <p className="text-sm text-gray-900 font-medium leading-relaxed">
                  {selectedVehicle.address && selectedVehicle.address !== "N/A"
                    ? selectedVehicle.address
                    : "Sin dirección disponible"}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">
                  Coordenadas:
                </h4>
                <p className="text-sm text-gray-900 font-medium">
                  {`${selectedVehicle.latitude.toFixed(6)}, ${selectedVehicle.longitude.toFixed(6)}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* No Vehicles Message - show when no units at all */}
        {vehicles.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50 z-20">
            <div className="bg-white rounded-xl p-8 text-center max-w-md mx-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-3xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Sin Unidades
              </h3>
              <p className="text-gray-500">
                Este link no tiene unidades configuradas o las unidades no están
                disponibles en este momento.
              </p>
            </div>
          </div>
        )}

        {/* Waiting for position data - show when units exist but no valid coordinates */}
        {vehicles.length > 0 && filteredVehicles.length === 0 && statusFilter === "all" && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50 z-20">
            <div className="bg-white rounded-xl p-8 text-center max-w-md mx-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-loader-4-line text-3xl text-blue-500 animate-spin"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Esperando datos de ubicación
              </h3>
              <p className="text-gray-500">
                Las unidades están configuradas pero aún no se han recibido datos de ubicación.
                Por favor espere unos momentos.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
